import Axios from 'axios';
import Vue from 'vue';
import i18n from '../../i18n';
import { API_HOST } from '../../config/app-const';
import { getUrlParams } from '../../helpers/pagination';

const state = {
  tags: {
    data: null,
    organizationIsBusy: false,
    categoryIsBusy: false,
    relationshipIsBusy: false,
    organizationData: null,
    categoryData: null,
    relationshipData: null,
    error: null,
    tagsIsBusy: false,
    pagination: null
  }
};

const mutations = {
  LOAD_ORGANIZATION_TAGS_BUSY: (state, payload) => {
    state.tags.organizationIsBusy = payload;
  },
  LOAD_ORGANIZATION_TAGS_SUCCESS: (state, payload) => {
    state.tags.organizationData = payload;
    state.tags.error = null;
  },
  LOAD_ORGANIZATION_TAGS_FAIL: (state, payload) => {
    state.tags.organizationData = null;
    state.tags.error = payload;
  },
  
  LOAD_TAGS_BUSY: (state, payload) => {
    state.tags.tagsIsBusy = payload;
  },
  LOAD_TAGS_SUCCESS: (state, {data, pagination}) => {
    if (pagination && pagination.currentPage === 1) state.tags.data = []
    state.tags.data = [...state.tags.data, ...data];
    state.tags.pagination = pagination;
    state.tags.error = null;
  },
  LOAD_TAGS_FAIL: (state, payload) => {
    state.tags.tags = null;
    state.tags.error = payload;
  },
  CLEAR_TAGS: (state) => {
    state.tags.data = null;
  },
  UPDATE_TAG: (state, payload) => {
    if (!state.tags.data || !state.tags.data.data.length) return;

    let tag = state.tags.data.data.find(tag => {
      return tag.id === payload.id;
    })

    if (tag) {
      tag.name = payload.name;
      tag.approved = payload.approved;
    }
  },
  
  LOAD_CATEGORY_TAGS_BUSY: (state, payload) => {
    state.tags.categoryIsBusy = payload;
  },
  LOAD_CATEGORY_TAGS_SUCCESS: (state, payload) => {
    let existingData;

    if (!payload.pagination) {
      existingData = payload;
    }
    else {
      existingData = state.tags.categoryData || { data: {} };
    }
    const newTags = payload.data;

    // If it's parent tag, replace the entire data
    if (payload.pagination && payload.pagination.currentPage === 1) {
      existingData.data = newTags;
    } else {
      // If it's not the parent tag, append the new data to the existing data
      existingData.data = { ...existingData.data, ...newTags };
    }
    state.tags.categoryData = {
      ...existingData,
      pagination: payload.pagination,
    };
    state.tags.error = null;
  },
  LOAD_RELATIONSHIP_TAGS_BUSY: (state, payload) => {
    state.tags.relationshipIsBusy = payload;
  },
  LOAD_RELATIONSHIP_TAGS_SUCCESS: (state, payload) => {
    state.tags.relationshipData = payload;
    state.tags.error = null;
  },
  LOAD_RELATIONSHIP_TAGS_FAIL: (state, payload) => {
    state.tags.relationshipData = null;
    state.tags.error = payload;
  }
};

const getters = {
  tags: state => {
    if (!state.tags.data) return [];

    return state.tags.data.data;
  },
  tagsIsBusy: state => {
    return state.tags.tagsIsBusy;
  }
}

const actions = {
  getOrganizationTags: async ({ commit, rootState }, { projectId, searchQuery, parentTagId }) => {
    commit('LOAD_ORGANIZATION_TAGS_BUSY', true);

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    let query = '';
    if (searchQuery) {
      query = `?query=${encodeURIComponent(searchQuery)}`;
    }

    if (parentTagId) {
      query += query ? `&parent_tag_id=${parentTagId}` : `?parent_tag_id=${parentTagId}`;
    }

    return Axios
      .get(`${API_HOST}/analysis/editor/${projectId}/tags/organization${query}`, config)
      .then(response => {
        commit('LOAD_ORGANIZATION_TAGS_SUCCESS', response.data)
        return response.data;
      })
      .catch(error => {
        Vue.notify({
          group: 'store_notifications',
          type: 'error',
          text: i18n.t('messages.request.error')});

        console.error(error);
        commit('LOAD_ORGANIZATION_TAGS_FAIL', error)
      })
      .finally(() => commit('LOAD_ORGANIZATION_TAGS_BUSY', false));
  },
  getTags: ({ commit, rootState }, { projectId, filters, key }) => {
    commit('LOAD_TAGS_BUSY', true);
    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };
    let urlParams = getUrlParams({ search: filters });

    let url = `${API_HOST}/analysis/${projectId}/categories`;
    if(key === 'relevancy'){
        urlParams = getUrlParams({ filters });
        url = `${API_HOST}/projects/${projectId}/tags`;
      }
    if (urlParams) url += `?${urlParams}`;

    return new Promise ((resolve, reject) => {
      Axios
        .get(url, config)
        .then(({data}) => {
          commit("LOAD_TAGS_SUCCESS", data.data);
        })
        .catch(error => {
          Vue.notify({
            group: 'store_notifications',
            type: 'error',
            text: i18n.t('messages.request.error')});
  
          console.error(error);
          commit('LOAD_TAGS_FAIL', error);

        })
        .finally(() => commit('LOAD_TAGS_BUSY', false));
    });
  },
  updateTag: ({ commit }, { tag }) => {
    commit('UPDATE_TAG', tag);
  },
  clearTags: ({ commit }) => {
    commit('CLEAR_TAGS');
  },
  getCategoryTags: ({ commit, rootState }, { projectId, pagination, categoryKey, searchQuery, shortcutSearch, filters = {} }) => {
    commit('LOAD_CATEGORY_TAGS_BUSY', true);

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };
    const categoryParent = categoryKey? `/${categoryKey}`: '';
    let query = ''

    if (searchQuery && searchQuery !== '' && shortcutSearch){
      query = `?query=${searchQuery}&shortcutSearch=${shortcutSearch}`; 
    }

    if (searchQuery && searchQuery !== '' && !shortcutSearch){
      query = `?query=${searchQuery}`;    
    }
    if (Object.keys(filters)?.length){ 
      query += `${query ? '&' : '?'}${getUrlParams({ search: filters })}`;
    }

    if (pagination){
      if (!query) query = '';
      query += `${query ? '&' : '?'}${Object.keys(pagination).map((param) => `${param}=${pagination[param]}`).join('&')}`
    }
    
    return new Promise ((resolve, reject) => {
      Axios
        .get(`${API_HOST}/analysis/editor/${projectId}/tags/category${categoryParent}${query}`, config)
        .then(response => {
          commit('LOAD_CATEGORY_TAGS_SUCCESS', response.data);
          resolve(response.data.data);
        })
        .catch(error => {
          Vue.notify({
            group: 'store_notifications',
            type: 'error',
            text: i18n.t('messages.request.error')});
  
          console.error(error);
          commit('LOAD_CATEGORY_TAGS_FAIL', error);

          reject();
        })
        .finally(() => commit('LOAD_CATEGORY_TAGS_BUSY', false));
    });
  },
  getRelationshipTags: ({ commit, rootState }, { projectId, searchQuery }) => {
    commit('LOAD_RELATIONSHIP_TAGS_BUSY', true);

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };
    let query = searchQuery && searchQuery !== ''? `?query=${searchQuery}`: '';

    Axios
      .get(`${API_HOST}/analysis/editor/${projectId}/tags/relationship${query}`, config)
      .then(response => {
        commit('LOAD_RELATIONSHIP_TAGS_SUCCESS', response.data)
      })
      .catch(error => {
        Vue.notify({
          group: 'store_notifications',
          type: 'error',
          text: i18n.t('messages.request.error')});

        console.error(error);
        commit('LOAD_RELATIONSHIP_TAGS_FAIL', error)
      })
      .finally(() => commit('LOAD_RELATIONSHIP_TAGS_BUSY', false));
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
};